import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TravelExplore } from '@mui/icons-material';
import { ICourtAdminstrativeUnitAssignment } from '../Interfaces/ICourtAdminstrativeUnitAssignment';
import {
  fetchBorisData,
  IBorisResult,
} from '../hooks/reactQuerys/RealEstateQueries';
import { IBlattFlurstueckNeu } from '../hooks/reactQuerys/GrundbuchblattQueries';

interface IProps {
  /** Vorbelegte Adresse (aus der ersten Adresse des Grundstücks). */
  defaultAddress?: string;
  courtAdminAssignments: ICourtAdminstrativeUnitAssignment[];
  /** Übernimmt das gefundene Flurstück als neues Flurstück des Grundstücks. */
  onApply: (patch: Partial<IBlattFlurstueckNeu>) => void;
}

const extractPlz = (s: string): string | null => {
  const m = s.match(/\b(\d{5})\b/);
  return m ? m[1] : null;
};

/** Brandenburg-PLZ-Bereiche (BORIS ist nur für Brandenburg verfügbar). */
const isBrandenburgPlz = (plz: string): boolean => {
  const n = Number(plz);
  if (isNaN(n)) return false;
  const r = (lo: number, hi: number) => n >= lo && n <= hi;
  return (
    r(1941, 1998) ||
    r(3000, 3253) ||
    r(4890, 4938) ||
    r(14400, 14999) ||
    r(15000, 16999) ||
    r(17258, 17291) ||
    r(19300, 19357)
  );
};

const parseAreaToNumber = (raw?: string | null): number | null => {
  if (!raw) return null;
  const cleaned = String(raw)
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.]/g, '');
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
};

/**
 * BORIS/ALKIS-Abruf (nur Brandenburg) für ein Grundstück: zieht zur Adresse
 * Gemarkung/Flur/Flurstück + Fläche + Bodenrichtwert und übernimmt das Flurstück
 * (Komfort — danach manuell korrigierbar). Der Bodenrichtwert wird angezeigt.
 */
export const BlattBorisLookup: React.FC<IProps> = ({
  defaultAddress,
  courtAdminAssignments,
  onApply,
}) => {
  const [address, setAddress] = useState(defaultAddress ?? '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IBorisResult | null>(null);

  const plz = extractPlz(address);
  const notBrandenburg = !!plz && !isBrandenburgPlz(plz);
  const canLookup = address.trim().length >= 3 && !notBrandenburg;

  const onLookup = async () => {
    setLoading(true);
    setResult(null);
    try {
      setResult(await fetchBorisData(address.trim()));
    } finally {
      setLoading(false);
    }
  };

  const fs = result?.flurstueck;
  const brw = result?.bodenrichtwert;
  const hasParcel =
    !!fs && !fs.fehler && (fs.gemarkung || fs.flur || fs.flurstuecksnummer);

  const onApplyClick = () => {
    if (!fs) return;
    const gem = (fs.gemarkung ?? '').trim().toLowerCase();
    let idAdministrativeUnit: number | undefined;
    if (gem) {
      const match = courtAdminAssignments.find(
        a =>
          a.idCourtRole === 3 &&
          (a.AdministrativeUnit ?? '').trim().toLowerCase() === gem
      );
      if (match) idAdministrativeUnit = match.idAdministrativeUnit;
    }
    onApply({
      Flur: fs.flur ?? '',
      Flurstuecksnummer: fs.flurstuecksnummer ?? '',
      idAdministrativeUnit: idAdministrativeUnit ?? null,
      Groesse: parseAreaToNumber(fs.flaeche_m2),
      Lage: fs.lage ?? '',
    });
  };

  return (
    <Box
      sx={theme => ({
        mt: 1.5,
        p: 1.5,
        borderRadius: 1,
        border: `1px dashed ${theme.palette.divider}`,
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 1 }}
      >
        <Typography variant='subtitle2' sx={{ whiteSpace: 'nowrap' }}>
          BORIS (Brandenburg)
        </Typography>
        <TextField
          size='small'
          label='Adresse für Katasterabruf'
          placeholder='Straße Nr, PLZ Ort'
          value={address}
          onChange={e => setAddress(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 220 }}
        />
        <Button
          variant='outlined'
          size='small'
          startIcon={
            loading ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              <TravelExplore />
            )
          }
          disabled={!canLookup || loading}
          onClick={onLookup}
        >
          Katasterdaten abrufen
        </Button>
      </Stack>

      <Typography variant='caption' color='text.secondary' display='block'>
        Bitte die vollständige Adresse mit Hausnummer und PLZ angeben (z. B. „Am
        Markt 1, 14467 Potsdam"). BORIS/ALKIS ist nur für Grundstücke in
        Brandenburg verfügbar; wird die Adresse nicht gefunden, bitte
        Schreibweise und PLZ prüfen.
      </Typography>

      {notBrandenburg && (
        <Typography variant='caption' color='text.secondary'>
          BORIS ist nur für Brandenburg verfügbar (erkannte PLZ {plz}).
        </Typography>
      )}

      {result?.fehler && (
        <Alert severity='warning' variant='outlined' sx={{ mt: 1 }}>
          {result.fehler}
        </Alert>
      )}

      {result && !result.fehler && (
        <Box sx={{ mt: 1 }}>
          {fs?.fehler || !hasParcel ? (
            <Alert severity='info' variant='outlined'>
              Kein eindeutiges Flurstück zur Adresse gefunden.
            </Alert>
          ) : (
            <Stack spacing={0.5}>
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                {fs?.gemarkung && (
                  <Chip size='small' label={`Gemarkung: ${fs.gemarkung}`} />
                )}
                {fs?.flur && <Chip size='small' label={`Flur: ${fs.flur}`} />}
                {fs?.flurstuecksnummer && (
                  <Chip
                    size='small'
                    label={`Flurstück: ${fs.flurstuecksnummer}`}
                  />
                )}
                {fs?.flaeche_m2 && (
                  <Chip size='small' label={`Fläche: ${fs.flaeche_m2} m²`} />
                )}
              </Stack>
              {brw && !brw.fehler && (
                <Typography variant='body2'>
                  <strong>Bodenrichtwert:</strong>{' '}
                  {brw.bodenrichtwert_anzeige ??
                    (brw.bodenrichtwert_eur_m2 != null
                      ? `${brw.bodenrichtwert_eur_m2} €/m²`
                      : '–')}
                  {brw.stichtag ? ` (Stichtag ${brw.stichtag})` : ''}
                  {brw.nutzungsart ? ` · ${brw.nutzungsart}` : ''}
                </Typography>
              )}
              <Box>
                <Button size='small' variant='contained' onClick={onApplyClick}>
                  Als Flurstück übernehmen
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};
